import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import {
  CreditCard,
  Loader2,
  FileText,
  Download,
  ExternalLink,
  Sparkles,
  AlertCircle,
  CalendarClock,
  RefreshCw,
} from "lucide-react";
import UpgradePlanDialog from "@/components/UpgradePlanDialog";
import AutoTopUpPanel from "@/components/AutoTopUpPanel";
import { useAuth } from "@/lib/auth";

const STATUS_TONE = {
  active: { bg: "bg-emerald-500/15", border: "border-emerald-400/40", text: "text-emerald-500 dark:text-emerald-400", label: "Active" },
  trialing: { bg: "bg-cyan-500/15", border: "border-cyan-400/40", text: "text-cyan-500 dark:text-cyan-400", label: "Trialing" },
  past_due: { bg: "bg-amber-500/15", border: "border-amber-400/40", text: "text-amber-600 dark:text-amber-300", label: "Past due" },
  canceled: { bg: "bg-rose-500/15", border: "border-rose-400/40", text: "text-rose-500 dark:text-rose-400", label: "Canceled" },
  incomplete: { bg: "bg-zinc-500/15", border: "border-zinc-400/40", text: "text-muted-foreground", label: "Incomplete" },
};

const fmtMoney = (cents, currency) =>
  ((cents || 0) / 100).toLocaleString("en-US", {
    style: "currency",
    currency: (currency || "usd").toUpperCase(),
    minimumFractionDigits: 2,
  });

const fmtDate = (unix) => (unix ? new Date(unix * 1000).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—");

export default function Billing() {
  const { user } = useAuth();
  const canWriteAutoTopUp = user && (user.role === "super_admin" || user.role === "tenant_admin");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [portalLoading, setPortalLoading] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/payments/subscription");
      setData(r.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load billing");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const openPortal = async () => {
    setPortalLoading(true);
    try {
      const r = await api.post("/payments/portal-session", {
        return_url: window.location.href,
      });
      window.location.href = r.data.portal_url;
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not open customer portal");
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 max-w-5xl mx-auto">
        <div className="panel rounded-2xl p-16 flex items-center justify-center text-muted-foreground text-sm">
          <Loader2 className="w-4 h-4 animate-spin mr-2" /> Loading billing…
        </div>
      </div>
    );
  }

  const sub = data?.subscription;
  const tone = sub ? (STATUS_TONE[sub.status] || STATUS_TONE.incomplete) : null;
  const hasStripe = !!data?.has_stripe_customer;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6" data-testid="billing-page">
      {/* Header */}
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-cyan-500 dark:text-cyan-300">§ Billing</div>
          <h1 className="mt-1 font-display font-black text-3xl sm:text-4xl tracking-tighter">Subscription &amp; invoices.</h1>
          <p className="text-sm text-muted-foreground mt-1 max-w-lg">
            Manage your plan, payment methods, invoices, and cancellation via Stripe&apos;s customer portal.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            onClick={load}
            variant="outline"
            data-testid="billing-refresh-btn"
            className="rounded-full h-10 gradient-stroke"
          >
            <RefreshCw className="w-3.5 h-3.5 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Current plan card */}
      <div
        className="panel-hero rounded-2xl p-6 flex items-start justify-between gap-6 flex-wrap"
        data-testid="current-plan-card"
      >
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Current plan</div>
            {tone && (
              <span className={`inline-flex items-center gap-1 rounded-full border ${tone.border} ${tone.bg} ${tone.text} px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest`}>
                <span className="w-1.5 h-1.5 rounded-full bg-current" /> {tone.label}
              </span>
            )}
            {data?.is_trial && !sub && (
              <span className="inline-flex items-center gap-1 rounded-full border border-cyan-400/40 bg-cyan-500/15 text-cyan-500 dark:text-cyan-400 px-2 py-0.5 text-[10px] font-mono uppercase tracking-widest">
                <Sparkles className="w-2.5 h-2.5" /> Trial
              </span>
            )}
          </div>

          <div className="mt-2 flex items-baseline gap-2 flex-wrap">
            <div className="font-display font-black text-3xl tracking-tighter">
              MOXYPLAY {(data?.plan || "trial").charAt(0).toUpperCase() + (data?.plan || "trial").slice(1)}
            </div>
            {sub && (
              <div className="text-muted-foreground text-sm font-mono">
                {fmtMoney(sub.amount_cents, sub.currency)} / {sub.interval}
              </div>
            )}
          </div>

          {sub && (
            <div className="mt-3 flex items-center gap-4 flex-wrap text-xs text-muted-foreground">
              <div className="flex items-center gap-1.5">
                <CalendarClock className="w-3.5 h-3.5" />
                Renews {fmtDate(sub.current_period_end)}
              </div>
              {sub.cancel_at_period_end && (
                <div className="flex items-center gap-1.5 text-amber-500 dark:text-amber-300">
                  <AlertCircle className="w-3.5 h-3.5" />
                  Cancels on {fmtDate(sub.current_period_end)}
                </div>
              )}
            </div>
          )}

          {data?.is_trial && data?.trial_ends_at && (
            <div className="mt-3 text-xs text-muted-foreground font-mono">
              Trial ends {new Date(data.trial_ends_at).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })}
            </div>
          )}
        </div>

        <div className="flex flex-col gap-2">
          {hasStripe ? (
            <Button
              onClick={openPortal}
              disabled={portalLoading}
              data-testid="open-portal-btn"
              className="tenant-btn rounded-full h-11 px-5 font-display font-bold text-sm"
            >
              {portalLoading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Opening…</> : <><ExternalLink className="w-4 h-4 mr-2" /> Manage subscription</>}
            </Button>
          ) : (
            <Button
              onClick={() => setUpgradeOpen(true)}
              data-testid="upgrade-from-billing-btn"
              className="tenant-btn rounded-full h-11 px-5 font-display font-bold text-sm"
            >
              <CreditCard className="w-4 h-4 mr-2" /> Upgrade to paid plan
            </Button>
          )}
          <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-right">
            {hasStripe ? "Managed by Stripe" : "Add a payment method to unlock"}
          </div>
        </div>
      </div>

      {/* Invoices */}
      <div className="panel rounded-2xl p-6" data-testid="invoices-card">
        <div className="flex items-center gap-2 mb-4">
          <FileText className="w-4 h-4 text-fuchsia-400" strokeWidth={1.75} />
          <div className="font-display font-bold text-lg">Recent invoices</div>
        </div>

        {(!data?.invoices || data.invoices.length === 0) ? (
          <div className="py-10 text-center text-sm text-muted-foreground border border-dashed border-border rounded-xl">
            No invoices yet. They&apos;ll appear here after your first paid billing cycle.
          </div>
        ) : (
          <div className="divide-y divide-border">
            {data.invoices.map((inv) => (
              <div
                key={inv.id}
                data-testid={`invoice-${inv.id}`}
                className="py-3 flex items-center justify-between gap-4 flex-wrap hover:bg-accent/30 -mx-3 px-3 rounded-md transition-colors"
              >
                <div className="min-w-0">
                  <div className="font-mono text-sm text-foreground">
                    {inv.number || inv.id.slice(0, 14) + "…"}
                  </div>
                  <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mt-0.5">
                    {fmtDate(inv.created)} · {inv.status}
                  </div>
                </div>
                <div className="flex items-center gap-4">
                  <div className="font-mono text-sm text-foreground">
                    {fmtMoney(inv.amount_paid, inv.currency)}
                  </div>
                  <div className="flex gap-1">
                    {inv.hosted_invoice_url && (
                      <a
                        href={inv.hosted_invoice_url}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`invoice-view-${inv.id}`}
                        className="p-2 rounded-sm border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                        title="View invoice"
                      >
                        <ExternalLink className="w-3.5 h-3.5" />
                      </a>
                    )}
                    {inv.invoice_pdf && (
                      <a
                        href={inv.invoice_pdf}
                        target="_blank"
                        rel="noopener noreferrer"
                        data-testid={`invoice-pdf-${inv.id}`}
                        className="p-2 rounded-sm border border-border text-muted-foreground hover:text-foreground hover:bg-accent transition-colors"
                        title="Download PDF"
                      >
                        <Download className="w-3.5 h-3.5" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Auto Top-Up section — payment method + threshold guard so uploads
          don't die when Cloudflare storage minutes run out overnight. */}
      <AutoTopUpPanel canWrite={canWriteAutoTopUp} />

      {/* Footer note */}
      <div className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground text-center pt-4">
        Powered by Stripe · payment methods stored securely by Stripe · never on our servers
      </div>

      <UpgradePlanDialog open={upgradeOpen} onClose={() => { setUpgradeOpen(false); load(); }} />
    </div>
  );
}
